import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import styles from './Auth.module.css'
import css from '@/components/common/Common.module.css'

interface FormData {
  companyName: string
  registeredName: string
  ownershipType: string
  companyRegNumber: string
  vatNumber: string
  addressLine1: string
  addressLine2: string
  postalCode: string
  businessPhone: string
  companyEmail: string
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  contactEmail: string
  requestedCreditLimit: string
  password: string
}

const initialForm: FormData = {
  companyName: '',
  registeredName: '',
  ownershipType: '',
  companyRegNumber: '',
  vatNumber: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  businessPhone: '',
  companyEmail: '',
  contactFirstName: '',
  contactLastName: '',
  contactPhone: '',
  contactEmail: '',
  requestedCreditLimit: '',
  password: '',
}

export function SignupForm() {
  const [form, setForm] = useState<FormData>(initialForm)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const { signUp } = useAuth()

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const metadata: Record<string, string> = {
      full_name: `${form.contactFirstName} ${form.contactLastName}`.trim(),
      company_name: form.companyName,
      registered_name: form.registeredName,
      ownership_type: form.ownershipType,
      company_registration_number: form.companyRegNumber,
      vat_number: form.vatNumber,
      address_line1: form.addressLine1,
      address_line2: form.addressLine2,
      postal_code: form.postalCode,
      business_phone: form.businessPhone ? `+44${form.businessPhone}` : '',
      company_email: form.companyEmail,
      contact_first_name: form.contactFirstName,
      contact_last_name: form.contactLastName,
      contact_phone: form.contactPhone ? `+44${form.contactPhone}` : '',
      contact_email: form.contactEmail,
      requested_credit_limit: form.requestedCreditLimit,
    }

    const { error } = await signUp(form.contactEmail, form.password, metadata)
    if (error) {
      setError(error.message)
    } else {
      setSuccess(true)
    }
    setLoading(false)
  }

  if (success) {
    return (
      <div className={styles.authPage}>
        <div className={styles.authCard}>
          <div className={styles.authHeader}>
            <div className={styles.successIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h1>Application Submitted</h1>
            <p style={{ lineHeight: 1.6 }}>
              Your trade account application has been submitted and is under review.
              We'll contact you at <strong>{form.contactEmail}</strong> once approved.
              This typically takes 1–2 business days.
            </p>
          </div>
          <div className={styles.authFooter}>
            <p><Link to="/gaviota/login">Back to sign in</Link></p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.registrationCard}>
        <div className={styles.authHeader}>
          <div className={styles.logo}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#0d1b2a"/>
              <text x="16" y="22" textAnchor="middle" fill="white" fontFamily="Space Grotesk" fontWeight="700" fontSize="18">G</text>
            </svg>
            <span>Gaviota</span>
          </div>
          <h1>Trade Account Application</h1>
          <p>Complete the form below to apply for a trade account</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.authForm}>
          {error && <div className={styles.error}>{error}</div>}

          {/* Company Information */}
          <h3>Company Information</h3>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Company Name *</label>
            <input className={css.formInput} value={form.companyName} onChange={set('companyName')} required />
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Registered Name (if different)</label>
            <input className={css.formInput} value={form.registeredName} onChange={set('registeredName')} />
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Type of Ownership</label>
              <select className={css.formSelect} value={form.ownershipType} onChange={set('ownershipType')}>
                <option value="">Select...</option>
                <option value="sole_trader">Sole Trader</option>
                <option value="partnership">Partnership</option>
                <option value="ltd">Limited Company (Ltd)</option>
                <option value="llp">Limited Liability Partnership (LLP)</option>
                <option value="plc">Public Limited Company (PLC)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Company Registration Number</label>
              <input className={css.formInput} value={form.companyRegNumber} onChange={set('companyRegNumber')} />
            </div>
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>VAT Number</label>
            <input className={css.formInput} value={form.vatNumber} onChange={set('vatNumber')} />
          </div>

          {/* Company Address */}
          <h3>Company Address</h3>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Address *</label>
            <input className={css.formInput} value={form.addressLine1} onChange={set('addressLine1')} required />
          </div>

          <div className={css.formGroup}>
            <label className={css.formLabel}>Street Address 2</label>
            <input className={css.formInput} value={form.addressLine2} onChange={set('addressLine2')} />
          </div>

          <div className={css.formGroup} style={{ maxWidth: 240 }}>
            <label className={css.formLabel}>Postal Code *</label>
            <input className={css.formInput} value={form.postalCode} onChange={set('postalCode')} required />
          </div>

          {/* Company Contact */}
          <h3>Company Contact</h3>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Business Phone Number</label>
              <div className={styles.phoneInputGroup}>
                <span className={styles.phonePrefix}>+44</span>
                <input className={`${css.formInput} ${styles.phoneInput}`} value={form.businessPhone} onChange={set('businessPhone')} type="tel" />
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Company Email</label>
              <input className={css.formInput} type="email" value={form.companyEmail} onChange={set('companyEmail')} />
            </div>
          </div>

          {/* Account Contact */}
          <h3>Account Contact</h3>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>First Name *</label>
              <input className={css.formInput} value={form.contactFirstName} onChange={set('contactFirstName')} required />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Last Name *</label>
              <input className={css.formInput} value={form.contactLastName} onChange={set('contactLastName')} required />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Contact Phone Number</label>
              <div className={styles.phoneInputGroup}>
                <span className={styles.phonePrefix}>+44</span>
                <input className={`${css.formInput} ${styles.phoneInput}`} value={form.contactPhone} onChange={set('contactPhone')} type="tel" />
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Contact Email * <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(login email)</span></label>
              <input className={css.formInput} type="email" value={form.contactEmail} onChange={set('contactEmail')} required />
            </div>
          </div>

          {/* Credit Application */}
          <h3>Credit Application</h3>

          <div className={css.formGroup} style={{ maxWidth: 240 }}>
            <label className={css.formLabel}>Requested Credit Limit</label>
            <input className={css.formInput} value={form.requestedCreditLimit} onChange={set('requestedCreditLimit')} placeholder="e.g. 5000" />
          </div>

          {/* Account Security */}
          <h3>Account Security</h3>

          <div className={css.formGroup} style={{ maxWidth: 360 }}>
            <label className={css.formLabel}>Password *</label>
            <input className={css.formInput} type="password" value={form.password} onChange={set('password')} required minLength={6} placeholder="At least 6 characters" autoComplete="new-password" />
          </div>

          <button type="submit" className={styles.submitBtn} disabled={loading} style={{ marginTop: 'var(--space-4)' }}>
            {loading ? 'Submitting application...' : 'Submit Application'}
          </button>
        </form>

        <div className={styles.authFooter}>
          <p>Already have an account? <Link to="/gaviota/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  )
}
